import { CurrentUser } from '@components/CurrentUser';
import { useAuthRedux } from '@hooks/useAuthRedux';
import { useMediaQuery } from '@hooks/useMediaQuery';
import { useUser } from '@hooks/useUser';
import { unit } from '@lib/style/unit';
import { classList } from '@lib/utils';
import Divider from '@material-ui/core/Divider';
import List from '@material-ui/core/List';
import DashboardIcon from '@material-ui/icons/Dashboard';
import DashboardOutlinedIcon from '@material-ui/icons/DashboardOutlined';
import HelpIcon from '@material-ui/icons/Help';
import HelpOutlineOutlinedIcon from '@material-ui/icons/HelpOutlineOutlined';
import { PAGE_HEADER_THEME } from '@packages/layout/lib/PageHeader/theme';
import { DRAWER_BREAKPOINT } from '@packages/layout/lib/UIShell/context';
import { createMemo } from '@packages/react';
import { IF } from '@packages/react/lib/if';
import { UserView } from '@packages/user';
import { useContext } from 'react';
import styled from 'styled-components';
import { Context } from '../UIShell/context';
import { SidebarItems } from './Items';
import { SidebarItem } from './SidebarItem';
import { SDrawer } from './style';

/**
 * @author: Dumitrana Alinus <alin.designstudio@gmail.com>
 * @class: Sidebar
 * @date:  08.10.2019
 * @time:  21:43
 */
export const Sidebar = createMemo(
    () => {
        const [state, { closeDrawer }] = useContext(Context);

        const user = useUser();
        const [auth] = useAuthRedux();
        const isMobile = useMediaQuery(`(max-width: ${DRAWER_BREAKPOINT}px)`);

        const hasUser = user && auth;

        /**
         * Close drawer
         */
        const handleClose = () => {
            if (isMobile) {
                closeDrawer();
            }
        };

        if (!hasUser) {
            return null;
        }

        const children = (
            <List className={'sidebar__list'} onClick={handleClose}>
                <UserView view={'customer'}>
                    <SidebarItem
                        label={'Dashboard'}
                        href={'dashboard.index'}
                        exact
                        icon={<DashboardOutlinedIcon style={{ marginLeft: '2px' }} />}
                        isActive={<DashboardIcon color={'secondary'} style={{ marginLeft: '2px' }} />}
                    />
                </UserView>

                <SidebarItems />

                <UserView view={'admin'}>
                    <SidebarItem
                        label={'Help Tutorials'}
                        href={'/help'}
                        icon={<HelpOutlineOutlinedIcon style={{ marginLeft: '2px' }} />}
                        isActive={<HelpIcon color={'secondary'} style={{ marginLeft: '2px' }} />}
                    />
                </UserView>
            </List>
        );

        return (
            <>
                {IF(
                    isMobile || state.getIn(['page', 'hideDrawer']),
                    <SDrawer
                        anchor={'left'}
                        open={state.getIn(['page', 'drawer'])}
                        onClose={closeDrawer}
                        ModalProps={{ keepMounted: true }}
                        className={'mobile active'}
                    >
                        {IF(
                            hasUser,
                            <>
                                <List className={'sidebar__list sidebar__profile'} onClick={handleClose}>
                                    <CurrentUser variant={'profile'} />
                                </List>
                                <Divider />
                            </>,
                        )}
                        <ScrollableMobileContent>{children}</ScrollableMobileContent>
                    </SDrawer>,
                )}

                {IF(
                    !isMobile && !state.getIn(['page', 'hideDrawer']),
                    <SDrawer
                        anchor={'left'}
                        open={state.getIn(['page', 'drawer'])}
                        variant={'persistent'}
                        className={classList('persistent', {
                            active: state.getIn(['page', 'drawer']),
                        })}
                    >
                        <Content>{children}</Content>
                    </SDrawer>,
                )}
            </>
        );
    },
    {
        displayName: 'Sidebar',
    },
);

const Content = styled.div`
    & {
        position: sticky;
        top: ${unit(PAGE_HEADER_THEME.get('size.desktop'))};
        overflow-x: hidden;
        overflow-y: auto;
        max-height: calc(100vh - 70px);
    }
`;

const ScrollableMobileContent = styled.div`
    & {
        overflow-y: auto;
    }
`;
